import { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import Actions from './Actions';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const ShippingAreaTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [areas, setAreas] = useState([]);
    const [regions, setRegions] = useState([]);
    const [regionFilter, setRegionFilter] = useState("");
    const [search, setSearch] = useState("");

    const { user } = useSelector((state) => state.user);
    const isSuperAdmin = user.role === "admin";
    const perms = user.adminRole?.permissions?.shippingAreas || {};
    const canCreate = isSuperAdmin || perms.create;
    const canEdit = isSuperAdmin || perms.edit;
    const canDelete = isSuperAdmin || perms.delete;

    const fetchAreas = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/shipping-areas');
            setAreas(data.areas);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load shipping areas", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    const fetchRegions = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/regions');
            setRegions(data.regions.map((r) => r.name));
        } catch (error) {
            // Non-critical — the region filter just stays empty if this fails
        }
    };

    useEffect(() => {
        fetchAreas();
        fetchRegions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteHandler = async (id) => {
        try {
            await axios.delete(`/api/v1/admin/shipping-area/${id}`);
            enqueueSnackbar("Shipping area deleted", { variant: "success" });
            setAreas((prev) => prev.filter((a) => a._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to delete area", { variant: "error" });
        }
    };

    const filteredAreas = areas
        .filter((a) => (regionFilter ? a.region === regionFilter : true))
        .filter((a) => (
            search.trim()
                ? a.name.toLowerCase().includes(search.trim().toLowerCase()) || a.region.toLowerCase().includes(search.trim().toLowerCase())
                : true
        ));

    const columns = [
        {
            field: "name",
            headerName: "Area",
            minWidth: 180,
            flex: 0.6,
        },
        {
            field: "region",
            headerName: "Region",
            minWidth: 160,
            flex: 0.5,
        },
        {
            field: "deliveryDays",
            headerName: "Delivery Days",
            minWidth: 140,
            flex: 0.5,
        },
        {
            field: "minOrderValue",
            headerName: "Min Order",
            type: "number",
            minWidth: 110,
            flex: 0.3,
            renderCell: (params) => <span>HK${params.row.minOrderValue}</span>,
        },
        {
            field: "shippingCharge",
            headerName: "Shipping Charge",
            type: "number",
            minWidth: 130,
            flex: 0.4,
            renderCell: (params) => <span>HK${params.row.shippingCharge}</span>,
        },
        {
            field: "active",
            headerName: "Active",
            minWidth: 90,
            flex: 0.3,
            renderCell: (params) => (
                params.row.active
                    ? <span className="text-green-700 bg-green-100 px-2 py-0.5 rounded-full text-xs font-medium">Active</span>
                    : <span className="text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full text-xs font-medium">Hidden</span>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 100,
            flex: 0.3,
            sortable: false,
            renderCell: (params) => (
                <Actions editRoute={"shipping-area"} deleteHandler={deleteHandler} id={params.row.id} name={params.row.name} canEdit={canEdit} canDelete={canDelete} />
            ),
        },
    ];

    const rows = filteredAreas.map((a) => ({
        id: a._id,
        name: a.name,
        region: a.region,
        deliveryDays: a.deliveryDays,
        minOrderValue: a.minOrderValue,
        shippingCharge: a.shippingCharge,
        active: a.active,
    }));

    return (
        <>
            <MetaData title="Admin: Shipping Areas | 1313 Mart" />
            {loading && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <h1 className="text-lg font-medium uppercase">Shipping Areas</h1>
                <div className="flex items-center gap-3">
                    <Link to="/admin/regions" className="py-2 px-4 rounded shadow font-medium text-primary-blue bg-white border border-primary-blue hover:shadow-lg">Manage Regions</Link>
                    {canCreate && (
                        <Link to="/admin/shipping-area/new" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">New Area</Link>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full overflow-hidden">

                <div className="flex flex-wrap items-center gap-3 p-4 border-b">
                    <TextField
                        label="Search by area or region"
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ minWidth: 240 }}
                    />

                    <TextField label="Region" select size="small" value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)} sx={{ minWidth: 200 }}>
                        <MenuItem value="">All Regions</MenuItem>
                        {regions.map((r) => <MenuItem value={r} key={r}>{r}</MenuItem>)}
                    </TextField>

                    {regionFilter && (
                        <Chip label={`Region: ${regionFilter}`} size="small" onDelete={() => setRegionFilter("")} />
                    )}
                    {search && (
                        <Chip label={`Search: ${search}`} size="small" onDelete={() => setSearch("")} />
                    )}
                </div>

                <div style={{ height: 600 }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        rowHeight={56}
                        pageSize={25}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        disableSelectIconOnClick
                        sx={{
                            border: 0,
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f9fafb', fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280' },
                            '& .MuiDataGrid-cell': { fontSize: '0.875rem' },
                            '& .MuiDataGrid-row:hover': { backgroundColor: '#f9fafb' },
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export default ShippingAreaTable;